/**
 * Single KPI donut ring (satellite) – Me vs Benchmark, unified green/orange.
 */
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";

const TRACK_STROKE_DARK = "rgba(255,255,255,0.25)";
const TRACK_STROKE_LIGHT = "rgba(0,0,0,0.12)";
const DEFAULT_ME_COLOR = "#35c79a";
const DEFAULT_BENCHMARK_COLOR = "#f0825c";

export default function KpiRing({
  label,
  variationA,
  variationB,
  isSelected,
  onSelect,
  size = 130,
  palette,
  meColor = DEFAULT_ME_COLOR,
  benchmarkColor = DEFAULT_BENCHMARK_COLOR,
}) {
  const [animatedA, setAnimatedA] = useState(0);
  const [animatedB, setAnimatedB] = useState(0);
  const isLight = Boolean(palette);
  const trackStroke = isLight ? (palette.trackStroke ?? TRACK_STROKE_LIGHT) : TRACK_STROKE_DARK;
  const textPrimary = isLight ? (palette.textPrimary ?? "#1a3d40") : "#fff";
  const textSecondary = isLight ? (palette.textSecondary ?? "#5a7a7d") : "rgba(255,255,255,0.7)";
  const deltaPositive = isLight ? (palette.deltaPositive ?? { bg: "rgba(45, 212, 191, 0.15)", color: "#0f766e" }) : { bg: "rgba(45, 212, 191, 0.15)", color: "#5eead4" };
  const deltaNegative = isLight ? (palette.deltaNegative ?? { bg: "rgba(248, 113, 113, 0.15)", color: "#dc2626" }) : { bg: "rgba(248, 113, 113, 0.2)", color: "#f87171" };
  const hoverBg = isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)";
  const focusRing = isLight ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.5)";

  useEffect(() => {
    const t = setTimeout(() => {
      setAnimatedA(variationA);
      setAnimatedB(variationB);
    }, 200);
    return () => clearTimeout(t);
  }, [variationA, variationB]);

  const strokeWidth = 8;
  const gap = 4;
  const radiusA = (size - strokeWidth) / 2;
  const radiusB = radiusA - strokeWidth - gap;
  const center = size / 2;
  const circumA = 2 * Math.PI * radiusA;
  const circumB = 2 * Math.PI * radiusB;
  const offsetA = circumA - (animatedA / 100) * circumA;
  const offsetB = circumB - (animatedB / 100) * circumB;

  const diff = variationA - variationB;
  const diffSign = diff >= 0 ? "+" : "";

  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.75,
        p: 1.5,
        cursor: "pointer",
        border: "none",
        borderRadius: 2,
        background: "transparent",
        outline: "none",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "scale(1.03)",
          bgcolor: hoverBg,
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 2px ${focusRing}`,
        },
        ...(isSelected && {
          transform: "scale(1.05)",
          bgcolor: `${meColor}1a`,
          boxShadow: `0 0 0 2px ${meColor}66`,
        }),
      }}
    >
      <Box position="relative">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))" }}
        >
          <circle
            cx={center}
            cy={center}
            r={radiusA}
            fill="none"
            stroke={trackStroke}
            strokeWidth={strokeWidth}
            opacity={0.6}
          />
          <circle
            cx={center}
            cy={center}
            r={radiusB}
            fill="none"
            stroke={trackStroke}
            strokeWidth={strokeWidth}
            opacity={0.6}
          />
          <circle
            cx={center}
            cy={center}
            r={radiusA}
            fill="none"
            stroke={meColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumA}
            strokeDashoffset={offsetA}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ transition: "all 1000ms ease-out" }}
          />
          <circle
            cx={center}
            cy={center}
            r={radiusB}
            fill="none"
            stroke={benchmarkColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumB}
            strokeDashoffset={offsetB}
            transform={`rotate(-90 ${center} ${center})`}
            opacity={0.85}
            style={{ transition: "all 1000ms ease-out" }}
          />
          <text
            x={center}
            y={center - 6}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fontSize: "1.05rem",
              fill: textPrimary,
              fontFamily: "Inter, Helvetica, Arial, sans-serif",
              fontWeight: 700,
            }}
          >
            {animatedA.toFixed(1)}%
          </text>
          <text
            x={center}
            y={center + 13}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fontSize: "0.6rem",
              fill: textSecondary,
              fontFamily: "Inter, Helvetica, Arial, sans-serif",
            }}
          >
            vs {animatedB.toFixed(1)}%
          </text>
        </svg>
      </Box>
      <Box
        component="span"
        sx={{
          maxWidth: 130,
          textAlign: "center",
          fontSize: "11px",
          fontWeight: 500,
          lineHeight: 1.3,
          color: isSelected ? meColor : textSecondary,
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          fontSize: "10px",
          fontWeight: 600,
          fontFamily: "monospace",
          ...(diff >= 0
            ? { bgcolor: deltaPositive.bg, color: deltaPositive.color }
            : { bgcolor: deltaNegative.bg, color: deltaNegative.color }),
        }}
      >
        {diffSign}
        {diff.toFixed(1)}pp
      </Box>
    </Box>
  );
}
